import { useDatePickerState as originalUseDatePickerState } from '@react-stately/datepicker';
import type {
  DatePickerStateOptions as OriginalDatePickerOptions,
  DatePickerState as OriginalDatePickerState,
} from '@react-stately/datepicker';
import { DateValue } from '@internationalized/date';
import { useRef } from 'react';
import { DateFieldBaseKeys, DatePickerStateKeys } from '../../utils/types';

type OriginalOnChange<T extends DateValue> = NonNullable<
  OriginalDatePickerOptions<T>['onChange']
>;

export type DatePickerStateOptions<
  T extends DateValue,
  P extends string,
  O extends OriginalOnChange<T> = OriginalOnChange<T>
> = Pick<OriginalDatePickerOptions<T>, DateFieldBaseKeys | 'isInvalid'> & {
  // We allow clearing the DateField and thus have to allow a null value
  // which is currently not part of the DatePickerProps type.
  onChange?: (
    value: Parameters<O>[0] | null,
    predefinedDate?: P
  ) => ReturnType<O>;
};

type OriginalSetDateValue = OriginalDatePickerState['setDateValue'];

export type DatePickerState<P extends string> = Pick<
  OriginalDatePickerState,
  DatePickerStateKeys
> & {
  setDateValue: (
    value: Parameters<OriginalSetDateValue>[0],
    predefinedDate?: P
  ) => ReturnType<OriginalSetDateValue>;
};

/**
 * A wrapper to allow for the onChange callback to have extra info
 **/
export const useDatePickerState = <P extends string>({
  onChange,
  ...options
}: DatePickerStateOptions<DateValue, P>): DatePickerState<P> => {
  const lastPredefinedDate = useRef<P | undefined>(undefined);

  const state = originalUseDatePickerState({
    ...options,
    onChange: (value) => {
      onChange?.(value, lastPredefinedDate.current);
      lastPredefinedDate.current = undefined;
    },
  });

  return {
    ...state,
    setDateValue: (value, predefinedDate) => {
      lastPredefinedDate.current = predefinedDate;
      state.setDateValue(value);
    },
  };
};
